import * as React from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  Box,
  Typography,
  Stack,
  Paper,
  Alert,
  Tooltip,
  IconButton,
  ClickAwayListener,
  Popper,
  Fade,
} from '@mui/material';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export default function Thankyou() {
  const { consentBundleId } = useParams();
  const navigate = useNavigate();

  const treatmentDetails = useQuery(
    ['treatmentDetails', consentBundleId],
    async () => {
      const { data } = await axios.get(`/consent_bundle/${consentBundleId}`);
      return data;
    },
    {
      onSuccess: data => {
        if (data.status === 'Draft') {
          navigate(`/${data.data.treatment_plan_id}`);
        }
      }
    }
  );

  // State for copy tooltip bubble
  const [copied, setCopied] = React.useState(false);
  const copyButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Optionally handle error
      });
  };

  const patientUrlBase = process.env.REACT_APP_PATIENT_URL || '';

  if (treatmentDetails.isLoading) {
    return (
      <Box sx={{ mt: 10, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (treatmentDetails.isError) {
    return (
      <Box sx={{ mt: 10, textAlign: 'center' }}>
        <Alert severity="error">Failed to load treatment details. Please try again later.</Alert>
      </Box>
    );
  }

  const data = treatmentDetails.data;
  if (!data || data.status === 'Draft') return null;

  const patientLink = data.link || `${patientUrlBase}/${consentBundleId}`;

  return (
    <Box
      sx={{
        maxWidth: 700,
        mx: 'auto',
        mt: 8,
        p: 4,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Stack spacing={3} alignItems="center">
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Thank You
        </Typography>

       

       <Paper variant="outlined" sx={{ width: '100%', p: 3 }}>
   <Typography 
  variant="h6" 
  fontWeight="medium" 
  gutterBottom
  sx={{ ml: 0.5 }} // moves it slightly right
>
  Patient Summary
</Typography>

{data.patient?.email_address && (
  <>
    <Alert
      icon={false}
      sx={{
        width: '96%',
        bgcolor: 'white',
        color: '#000000ff',
        fontWeight: '500',
        textAlign: 'left',
        ml: 0,           // remove any left margin
        pl: 0.5,
      }}
    >
      We have shared this link with your patient to give them access to the documents.
      {/* <strong> {data.patient.first_name} {data.patient?.last_name}</strong> */}
    </Alert>
  </>
)}

{!data.patient?.email_address && (
  <Alert
    icon={<ErrorOutlineIcon />}
    severity="warning"
    variant="filled"
    sx={{
      width: '96%',
      fontWeight: 'bold',
      textAlign: 'left',
    }}
  >
    We can't find an email for{' '}
    <strong>
      {data.patient?.title} {data.patient?.first_name} {data.patient?.last_name}
    </strong>.
    Please share this link with your patient.
  </Alert>
)}


  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      bgcolor: '#f5f5f7',
      borderRadius: 2,
      p: '8px 12px',
      userSelect: 'all',
      wordBreak: 'break-all',
      mt: 2,
    }}
  >
    <Typography
      sx={{
        flexGrow: 1,
        color: 'text.primary',
        fontWeight: 500,
      }}
    >
      {patientLink}
    </Typography>

    {/* Copy button */}
    <Box sx={{ position: 'relative', ml: 1 }}>
      <IconButton
        ref={copyButtonRef}
        size="small"
        sx={{
          bgcolor: '#f06000ff',
          color: 'white',
          '&:hover': { bgcolor: '#3f65f5' },
          width: 32,
          height: 32,
          borderRadius: 2,
        }}
        onClick={() => copyToClipboard(patientLink)}
        aria-label="copy patient link"
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>

      {/* Copied bubble */}
      <Popper
        open={copied}
        anchorEl={copyButtonRef.current}
        placement="top"
        transition
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 6],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Box
              sx={{
                bgcolor: '#5c87ff',
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 'medium',
                boxShadow: 2,
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Copied
            </Box>
          </Fade>
        )}
      </Popper>
    </Box>
  </Box>
</Paper>

      </Stack>
    </Box>
  );
}
